import { useMutation } from "@apollo/client";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { gql } from "generated-graphql";
import { BulkCreateCersRevisionsMutation } from "generated-graphql/graphql";
import { useGridSelection } from "hooks/omnisearch-v2";
import { useCallback, useState } from "react";

import { StaffCersRejectionFindingRow, isFindingSelectable } from "./rejectionStatus";

export const BULK_CREATE_CERS_REVISIONS = gql(`
  mutation BulkCreateCersRevisions($findingIds: [ID!]!) {
    bulkCreateCersRevisions(findingIds: $findingIds) {
      created
      linked
      failed
      createdReportIds
      failures {
        findingId
        facilityName
        reason
      }
    }
  }
`);

type BulkResult = BulkCreateCersRevisionsMutation["bulkCreateCersRevisions"];

interface DialogProps {
  open: boolean;
  rows: StaffCersRejectionFindingRow[];
  /** Total selected IDs, which can exceed the resolvable snapshot rows. */
  selectedCount: number;
  onClose: () => void;
  /** Called after a completed run is dismissed — clears the grid selection. */
  onDone: () => void;
}

/** Confirm + results dialog. Exported separately so tests can drive it with
 *  props instead of a mounted grid selection. */
export function BulkCreateRevisionsDialog({
  open,
  rows,
  selectedCount,
  onClose,
  onDone,
}: DialogProps) {
  const [result, setResult] = useState<BulkResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [run, { loading }] = useMutation(BULK_CREATE_CERS_REVISIONS, {
    // One operation feeds both the grid and the stat cards, so a single
    // refetch rescopes everything.
    refetchQueries: ["StaffCersRejectionFindings"],
    awaitRefetchQueries: true,
  });

  const facilityCount = new Set(rows.map((r) => r.encampFacilityId)).size;

  const handleRun = useCallback(async () => {
    setRunError(null);
    try {
      const { data } = await run({ variables: { findingIds: rows.map((r) => r.id) } });
      setResult(data?.bulkCreateCersRevisions ?? null);
    } catch {
      setRunError("Bulk revision creation failed — nothing may have been saved. Try again.");
    }
  }, [run, rows]);

  const handleClose = useCallback(() => {
    // Escape/backdrop-close must respect the in-flight mutation like the
    // Cancel button does: closing mid-run would let the late result land on
    // a closed dialog and resurface as a stale results screen next open.
    if (loading) return;
    const finished = result != null;
    setResult(null);
    setRunError(null);
    onClose();
    if (finished) {
      onDone();
    }
  }, [loading, result, onClose, onDone]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create revisions</DialogTitle>
      <DialogContent>
        {result == null ? (
          <Stack spacing={2}>
            <Typography>
              {rows.length} finding{rows.length === 1 ? "" : "s"} across {facilityCount} facilit
              {facilityCount === 1 ? "y" : "ies"}.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Findings on the same facility share one revision report. Facilities that already have
              an open revision are linked to it instead of creating another.
            </Typography>
            {selectedCount > rows.length ? (
              <Alert severity="info">
                {selectedCount - rows.length} selected row
                {selectedCount - rows.length === 1 ? " isn't" : "s aren't"} actionable and
                won&apos;t be included.
              </Alert>
            ) : null}
            {runError ? <Alert severity="error">{runError}</Alert> : null}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography>
              {result.created} revision{result.created === 1 ? "" : "s"} created.
            </Typography>
            {result.linked > 0 ? (
              <Typography>{result.linked} linked to an existing open revision.</Typography>
            ) : null}
            {result.failures.length > 0 ? (
              <>
                <Alert severity="warning">
                  {result.failed} finding{result.failed === 1 ? "" : "s"} could not be resolved.
                </Alert>
                <List dense>
                  {result.failures.map((f) => (
                    <ListItem key={f.findingId} disableGutters>
                      <ListItemText
                        primary={f.facilityName ?? "Unknown finding"}
                        secondary={f.reason}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {result == null ? (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            {/* rows can be empty while selectedCount > 0: every selected id was
                stale/off-page. Sending [] would just bounce off the server. */}
            <Button variant="contained" onClick={handleRun} disabled={loading || rows.length === 0}>
              Create revisions
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Command-bar button wired to the grid selection. */
export function BulkCreateRevisions() {
  const { selectedCount, getSelectedRows, clearSelection } =
    useGridSelection<StaffCersRejectionFindingRow>();
  const [open, setOpen] = useState(false);
  // Snapshot the selection when the dialog opens so it can't shift mid-flow.
  const [pendingRows, setPendingRows] = useState<StaffCersRejectionFindingRow[]>([]);

  const handleOpen = useCallback(() => {
    // Defense in depth: the grid already gates selection via isRowSelectable.
    // Null-guard first: under server pagination, selected IDs the grid can no
    // longer resolve to a loaded row come back as undefined in the rows map.
    const rows = getSelectedRows()
      .filter((r): r is StaffCersRejectionFindingRow => r != null)
      .filter(isFindingSelectable);
    setPendingRows(rows);
    setOpen(true);
  }, [getSelectedRows]);

  return (
    <>
      <Button variant="contained" disabled={selectedCount === 0} onClick={handleOpen}>
        Create revisions{selectedCount > 0 ? ` (${selectedCount})` : ""}
      </Button>
      <BulkCreateRevisionsDialog
        open={open}
        rows={pendingRows}
        selectedCount={selectedCount}
        onClose={() => setOpen(false)}
        onDone={clearSelection}
      />
    </>
  );
}
